<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments, status } =
  useMessageContext();

// Mensagem otimista (upload em andamento): nunca mostrar "indisponivel" —
// o skeleton cobre ate o anexo real chegar.
const inProgress = computed(() => status?.value === 'progress');

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry({
  max_retry: 4,
  backoff: 800,
});

const showGallery = ref(false);
const isDownloading = ref(false);
const showError = computed(() => hasError.value && !inProgress.value);
const isLoading = computed(() => !isLoaded.value && !showError.value);

const displayUrl = computed(
  () => attachment.value?.thumbUrl || attachment.value?.dataUrl
);

onMounted(() => {
  if (displayUrl.value) {
    loadWithRetry(displayUrl.value, attachment.value?.id);
  }
});

watch(displayUrl, newUrl => {
  if (newUrl && !isLoaded.value && !hasError.value) {
    loadWithRetry(newUrl, attachment.value?.id);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    toast.error(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <!-- Loading skeleton -->
    <div v-if="isLoading" class="rounded-lg overflow-hidden">
      <div
        class="w-48 h-36 bg-n-alpha-2 rounded-lg animate-pulse flex items-center justify-center"
      >
        <Icon
          icon="i-lucide-image"
          class="size-8 text-n-slate-9 animate-bounce"
        />
      </div>
    </div>
    <!-- Error state -->
    <div
      v-else-if="showError"
      class="flex items-center gap-1 text-center rounded-lg"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <!-- Loaded state -->
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu max-w-[320px] max-h-[320px] object-contain"
        :src="displayUrl"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
